// src/app/core/auth/auth.service.ts
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { HttpHelperService } from '../helper/http-helper.service';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { APP_ROUTES } from '../constants/app-routes';

interface UserData {
  token: string;
  data: {
    userId: string;
    email: string;
    [key: string]: any; // Flexible for additional user properties
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private loginApiEndpoint = API_ENDPOINTS.auth.login;
  private forgotPasswordApiEndpoint = API_ENDPOINTS.auth.forgotPassword;
  private resetPasswordApiEndpoint = API_ENDPOINTS.auth.resetpassword;
  private refreshTokenApiEndpoint = API_ENDPOINTS.auth.refreshToken

  private userDataKey = 'user'; // Key for localStorage

  constructor(private httpHelper: HttpHelperService, private router: Router) { }

  login(email: string, password: string): Observable<UserData> {
    localStorage.removeItem(this.userDataKey); // Remove the entire user object
    return this.httpHelper.post<UserData>(this.loginApiEndpoint, { email, password }).pipe(
      tap(response => {
        if (response && response.data) {
          this.setUserData(response.data);
        }
      })
    );
  }
  switchOrganization(organizationId: number): Observable<any> {
    return this.httpHelper.post<any>(API_ENDPOINTS.auth.switchOrganization(organizationId), {}).pipe(
      tap(response => {
        if (response && response.data) {
          const currentUserData = this.getUserData();
          if (currentUserData) {
            const updatedUserData = {
                ...currentUserData,
                organizationId: response.data.organization_id,
                token: response.data.token,
                refreshtoken: response.data.refresh_token
            };
            this.setUserData(updatedUserData);
          }
        }
      }));
  }
  setUserData(data: any) {
    localStorage.setItem(this.userDataKey, JSON.stringify(data));
  }
  forgotPassword(email: string): Observable<any> {
    return this.httpHelper.post<any>(this.forgotPasswordApiEndpoint, { email }).pipe(
      tap(() => {
      })
    );
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.httpHelper.post<any>(this.resetPasswordApiEndpoint, { token, password: newPassword }).pipe(
      tap(() => {
        // Clear any stored data and redirect to login after successful reset
        localStorage.removeItem(this.userDataKey);
        // this.router.navigate(['/', APP_ROUTES.auth.base, APP_ROUTES.auth.login]);
      })
    );
  }
  refreshToken(): void{
    const body = {
      token: JSON.parse(localStorage.getItem("user") || '').refreshtoken
    }
    this.httpHelper.post<any>(this.refreshTokenApiEndpoint, body).subscribe
      ({
        next:(value) => {
          const user = {
            "id": value.data.id,
            "name": value.data.name,
            "email": value.data.email,
            "role_id": value.data.role_id,
            "type": value.data.type,
            "ui_permissions": value.data.ui_permissions,
            "refreshtoken": value.data.newRefreshToken,
            "token": value.data.newToken
          }
          this.setUserData(user)
          window.location.reload()
        },
        error:(err) => {
          localStorage.clear()
          window.location.reload()
        },
      })
  }
  getToken(): string | null {
    const userData = localStorage.getItem(this.userDataKey);
    if (userData) {
      try {
        const parsedData: UserData = JSON.parse(userData);
        return parsedData.token || null;
      } catch (e) {
        console.error('Error parsing user data from localStorage:', e);
        return null;
      }
    }
    return null;
  }
  getUserData(): UserData['data'] | null {
    const userData = localStorage.getItem(this.userDataKey);
    if (userData) {
      try {
        const parsedData = JSON.parse(userData);
        return parsedData || null; // parsedData is already the `data` object
      } catch (e) {
        console.error('Error parsing user data from localStorage:', e);
        return null;
      }
    }
    return null;
  }
  isLoggedIn(): boolean {
    const user = this.getUserData();
    const token = user ? user['token'] : null;
    const orgId = user ? user['organizationId'] : null;

    if(token && orgId){
      return true;
    }

    return false;
  }

  logout(): void {
    localStorage.removeItem(this.userDataKey); // Remove the entire user object
    this.router.navigate(['/', APP_ROUTES.auth.base, APP_ROUTES.auth.login]);
  }

  checkPermission(resourceKey: string, action: string): boolean {
    const user = this.getUserData();
    if (!user) return false;

    const permissions = user['ui_permissions'];
    if (!permissions || typeof permissions !== 'object') return false;

    const resourcePermissions = permissions[resourceKey];
    if (!resourcePermissions) return false;

    return Boolean(resourcePermissions[action]);
  }
}